"use client";

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { type DeepReadonly } from "@/utils/tsmagic";
import DOMPurify from "isomorphic-dompurify";
import { Sequence, useVideoConfig } from "remotion";
import { type z } from "zod";

import {
  BOUNDS_COLORS,
  BoundsDebugContext,
  EDITABLE_OUTLINE_COLOR,
  boundsStatus,
} from "../core/boundsDebug";
import { type RichTextCardObjectSchema } from "../core/derivedSchema";
import { useCurrentFrameStyle } from "../core/useCurrentFrameStyle";
import {
  NO_SCALE,
  RICHTEXT_DEFAULT_FONT_PX,
  WRAP_DEFAULT_WIDTH_PCT,
  resolveRichTextWidthPx,
  richTextWhiteSpace,
} from "./richTextLayout";

export type RichTextCardObject = z.infer<typeof RichTextCardObjectSchema>;
export type ReadOnlyRichTextCardObject = DeepReadonly<RichTextCardObject>;

export const RichTextCardObjectComponent: React.FC<{
  cardObject: ReadOnlyRichTextCardObject;
}> = ({ cardObject }) => {
  const { outerStyle, innerStyle, transform: frameTransform } =
    useCurrentFrameStyle(cardObject);
  // The wrap box lives INSIDE the Remotion composition (the Player/Lambda scales
  // the whole composition to fit its display area), so the correct,
  // device-independent width source is the composition width — NOT a measured
  // on-screen `offsetWidth`, which was `0` in `/view` before layout settles (so
  // wrapping never engaged in playback) and display-size-dependent everywhere
  // else (so a card wrapped at a different point per device). The editor's
  // edit-box still uses the on-screen width and stays aligned because the
  // Player scales composition px → display px proportionally.
  //
  // Composition width alone is NOT enough, which is what broke staging on
  // 2026-10-01: the box moved into composition space but the font did not, and
  // CSS `scale:` (applied after layout) magnified the finished line. The helper
  // now divides by the object's scale so the text is laid out in the same space
  // its font is measured in. See richtext-playback-wrap-fix-lld.
  const { width: compositionWidth, height: compositionHeight } =
    useVideoConfig();
  // Scale compensation is for WRAP-ON objects only, and the gate is load-bearing.
  //
  // Compensation exists so a wrapped sentence breaks at the same word on both
  // surfaces. A wrap-off object is `nowrap` — it has no wrap point — so the
  // compensation buys nothing there, and it is not free: the box is centred on
  // its anchor by `translate(-50%, -50%)` (useCurrentFrameStyle), so dividing it
  // by the scale pulls both edges inward. At 40% width, composition 1080 and
  // scale 500 the box drops 432px → 86.4px, and because `nowrap` text overflows
  // rather than reflowing, left-aligned text would start 864 composition px
  // further right than it does today. Centred text is unaffected, which is how
  // this stayed invisible until Copilot raised it on #783.
  //
  // Passing NO_SCALE for wrap-off reproduces the pre-#783 formula exactly, since
  // resolveRichTextWidthPx(w, cw, 100) === (w / 100) * cw. So stored geometry
  // cannot move. Pinned by the wrap-off geometry guard in
  // RichTextCardObject.render.test.tsx — the editor has always gated this the
  // same way; only this call site did not.
  //
  // RESTING scale, not `frameTransform.scale`. The per-frame value includes
  // animation, so wrapping from it would re-flow the words every frame a
  // scale animation ran. Animating a text box must MOVE it, not re-wrap it.
  //
  // The wrap-on branch defaults a missing width; the wrap-off branch must not,
  // because there "no width" means an auto box that sizes to its content.
  const width = cardObject.metadata.wrap
    ? resolveRichTextWidthPx(
        cardObject.width ?? WRAP_DEFAULT_WIDTH_PCT,
        compositionWidth,
        cardObject.transform.scale,
      )
    : resolveRichTextWidthPx(cardObject.width, compositionWidth, NO_SCALE);
  const html = useMemo(
    () => DOMPurify.sanitize(cardObject.metadata.rawHTML),
    [cardObject.metadata.rawHTML],
  );

  // Debug bounding-box overlay (editor surfaces only; off in /view + Lambda via
  // the default-false context). See core/boundsDebug + LLD §6.6.
  const surface = useContext(BoundsDebugContext);
  // An ARTIST sees every box, because they are auditing the whole card. A
  // CONSUMER only sees the one they can act on — outlining text they cannot
  // touch is noise. `isEditable` already means "consumer-touchable" across the
  // app (the consumer editor filters its list on it; Endplate clears it on the
  // watermark), so this reuses that flag rather than adding a parallel one.
  const outlineVisible =
    surface === "artist" || (surface === "consumer" && cardObject.isEditable);
  // On the artist surface, consumer-editable text is called out in green and a
  // little thicker, so "a consumer will type here" reads at a glance.
  const highlightEditable = surface === "artist" && cardObject.isEditable;
  const innerRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<{
    boxW: number;
    boxH: number;
    contentW: number;
    contentH: number;
  } | null>(null);
  useEffect(() => {
    if (!outlineVisible || !innerRef.current) return;
    const el = innerRef.current;
    setMeasured({
      boxW: el.offsetWidth,
      boxH: el.offsetHeight,
      contentW: el.scrollWidth,
      contentH: el.scrollHeight,
    });
    // `wrap` flips whiteSpace, which changes scroll/offset sizes → re-measure.
  }, [outlineVisible, html, width, cardObject.metadata.wrap]);
  const status =
    outlineVisible && measured
      ? boundsStatus({
          // Classify with the CURRENT-FRAME transform so color matches the drawn
          // box (incl. rotation + any animation at this frame), not the base one.
          anchorX: frameTransform.x,
          anchorY: frameTransform.y,
          scale: frameTransform.scale,
          rotate: frameTransform.rotate,
          resolutionX: compositionWidth,
          resolutionY: compositionHeight,
          ...measured,
        })
      : null;
  // Divide by the object's own scale so the outline reads at its intended
  // weight on screen rather than being magnified with the text.
  const debugOutline: React.CSSProperties = status
    ? {
        outline: `${(highlightEditable ? 4 : 2) / (frameTransform.scale / 100)}px solid ${
          highlightEditable ? EDITABLE_OUTLINE_COLOR : BOUNDS_COLORS[status]
        }`,
        outlineOffset: 0,
      }
    : {};

  return (
    <Sequence
      from={cardObject.sequence.from}
      durationInFrames={cardObject.sequence.durationInFrames}
      layout="none"
    >
      <div style={outerStyle}>
        <div
          ref={innerRef}
          dangerouslySetInnerHTML={{ __html: html }}
          style={{
            width,
            // Base size for text with no inline size of its own. Setting it
            // is the point: without it this div inherited from whatever page
            // drew the card, so the same card could lay out differently on
            // different surfaces (a G0a violation). The value is what it was
            // already inheriting in practice, so no delivered card re-renders.
            // Inline sizes on descendant spans still win, so toolbar-authored
            // text is unaffected either way.
            fontSize: RICHTEXT_DEFAULT_FONT_PX,
            ...debugOutline,
            // Wrapping is opt-in per object via `metadata.wrap` (default off),
            // so every existing/sent object keeps its current single-line
            // `nowrap` render — nothing changes retroactively. When an artist
            // turns wrap on, text wraps within the width box; overflowWrap
            // stops a single long word from bleeding past it. Overflow policy:
            // we do NOT clip (no overflow:hidden) — clipping would drop words.
            // (text-rendering & typography LLD §6.1.)
            whiteSpace: richTextWhiteSpace(cardObject.metadata.wrap),
            overflowWrap: "break-word",
            ...innerStyle,
          }}
        />
      </div>
    </Sequence>
  );
};
